// Slack API surface.
//
// Thin layer over the generated client (for `POST /slack/oauth/callback`,
// which is the only Slack endpoint S5 covered in OpenAPI 3.1 today) plus
// hand-rolled fetch wrappers for the three endpoints S8 needs that S5 has
// not yet added to the spec:
//
//   - `POST /slack/oauth/start`     — server returns Slack `authorize_url` + state
//   - `GET  /slack/binding/status`  — server returns current bind state for the caller
//   - `DELETE /slack/binding`       — server unbinds and returns 204
//
// All four are mocked by `src/api/mocks/handlers/slack.ts`.
//
// CONTRACT: per CLAUDE.md hard constraint #1, this file NEVER reads or
// writes a Slack token. The Midway HttpOnly cookie carries auth; OAuth
// tokens stay server-side. The browser only sees the binding-status JSON.
// The popup orchestration uses `window.open` + `postMessage`; it does not
// touch the OAuth token at any point.

import type { SlackOauthCallbackResponse } from './generated';
import { ApiError, RateLimitedError, redirectToLogin } from './client';

const API_BASE = '/api';

/**
 * Origin used for the OAuth popup `postMessage` exchange. Production
 * defaults to `window.location.origin`; tests override via the
 * `expectedOrigin` arg on `listenForBindMessage`.
 */
export function popupOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}

/** Server response from `POST /api/slack/oauth/start`. */
export interface SlackOauthStartResponse {
  readonly authorizeUrl: string;
  readonly state: string;
}

/** Server response from `GET /api/slack/binding/status`. */
export interface SlackBindingStatus {
  readonly bound: boolean;
  readonly slackUserId?: string;
  readonly slackUserDisplayName?: string;
  readonly slackTeamId?: string;
  readonly slackTeamName?: string;
  readonly defaultChannel?: string;
  readonly boundAt?: string;
  readonly employeeId?: string;
}

/** Browser-side `postMessage` shape posted by the OAuth callback page. */
export interface SlackBindMessage {
  readonly type: 'slack:bound';
  readonly employeeId: string;
  readonly slackUserId: string;
  readonly slackTeamId?: string;
  readonly slackUserDisplayName?: string;
}

export interface SlackBindErrorMessage {
  readonly type: 'slack:bind-error';
  readonly error: string;
}

export type SlackPopupMessage = SlackBindMessage | SlackBindErrorMessage;

/**
 * Read the `wayne_csrf_token` cookie, mirror it into `X-CSRF-Token`. Same
 * pattern as `client.ts` but inlined so we don't pull a hard dependency on
 * the singleton-config interceptors during a popup or while the page is
 * still booting.
 */
function readCsrf(): string {
  if (typeof document === 'undefined') return '';
  for (const cookie of document.cookie.split(';')) {
    const trimmed = cookie.trim();
    const eq = trimmed.indexOf('=');
    if (eq === -1) continue;
    if (trimmed.substring(0, eq).trim() === 'wayne_csrf_token') {
      try {
        return decodeURIComponent(trimmed.substring(eq + 1));
      } catch {
        return '';
      }
    }
  }
  return '';
}

/**
 * Common fetch wrapper: cookie auth, CSRF on state-changing methods, 401
 * → Midway redirect, 429 → typed error, generic error message otherwise.
 */
async function slackFetch(
  path: string,
  init: RequestInit & { method?: string } = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET') {
    const csrf = readCsrf();
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    method,
    headers,
    credentials: 'include',
  });
  if (response.ok) return response;
  if (response.status === 401) {
    redirectToLogin();
    throw new ApiError('Authentication required', 401);
  }
  if (response.status === 429) {
    const retry = Number.parseInt(response.headers.get('Retry-After') ?? '5', 10);
    throw new RateLimitedError(Number.isFinite(retry) ? retry : 5);
  }
  throw new ApiError('Request failed', response.status);
}

/** `POST /api/slack/oauth/start`. */
export async function startSlackOauth(): Promise<SlackOauthStartResponse> {
  const response = await slackFetch('/slack/oauth/start', { method: 'POST' });
  return (await response.json()) as SlackOauthStartResponse;
}

/**
 * `POST /api/slack/oauth/callback`. The OpenAPI spec exposes this as
 * `slackOauthCallback`, but the generated SDK currently returns the
 * legacy `{ data, error }` envelope, which surfaces fetch-rejections as
 * a thrown error rather than a `result.error`. We use the same hand-
 * rolled `slackFetch` wrapper as the other three endpoints to keep the
 * error semantics consistent across the four-endpoint Slack surface.
 */
export async function completeSlackOauth(
  code: string,
  state: string,
): Promise<SlackOauthCallbackResponse> {
  const response = await slackFetch('/slack/oauth/callback', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  });
  return (await response.json()) as SlackOauthCallbackResponse;
}

/** `GET /api/slack/binding/status`. */
export async function getSlackBindingStatus(): Promise<SlackBindingStatus> {
  const response = await slackFetch('/slack/binding/status');
  return (await response.json()) as SlackBindingStatus;
}

/** `DELETE /api/slack/binding`. */
export async function deleteSlackBinding(): Promise<void> {
  await slackFetch('/slack/binding', { method: 'DELETE' });
}

/**
 * Open the Slack OAuth popup. Returns the popup window handle if the popup
 * opened successfully, or `null` if the browser blocked it (the caller
 * should fall back to a same-tab navigation). The actual auth happens in
 * the popup; this function does NOT wait for the result — callers attach
 * a `postMessage` listener via `listenForBindMessage` first.
 */
export function openSlackOauthPopup(authorizeUrl: string): Window | null {
  if (typeof window === 'undefined') return null;
  const features = 'width=600,height=720,menubar=no,toolbar=no,location=yes';
  return window.open(authorizeUrl, 'wayne_slack_oauth', features);
}

/**
 * Subscribe to `postMessage` events posted by the Slack callback page once
 * the bind succeeds (or fails). Returns an unsubscribe function. Caller
 * MUST tear it down (e.g., on mutation settle) so leaked listeners don't
 * pile up across reconnect cycles.
 *
 * Origin check: per CLAUDE.md hard constraint and the prompt's
 * "Hard rules" §3, we drop any message whose `event.origin` is not the
 * SPA's own origin. The popup is loaded from the same origin as the
 * opener, so an attacker hosting a fake page on a different origin
 * cannot satisfy this check.
 */
export function listenForBindMessage(
  onMessage: (message: SlackPopupMessage) => void,
  expectedOrigin: string = popupOrigin(),
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (event: MessageEvent) => {
    if (expectedOrigin && event.origin !== expectedOrigin) return;
    const data = event.data as Partial<SlackPopupMessage> | undefined;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'slack:bound' || data.type === 'slack:bind-error') {
      onMessage(data as SlackPopupMessage);
    }
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
